// Request limits are validated in the UI. Staff must verify eligibility and
// quantities before fulfillment; a contact request is not an authorized order.
export function selectionTotal(items, quantities) {
  return Object.entries(quantities).reduce((total, [id, qty]) => total + qty * (items.get(id)?.pieces || 1), 0);
}

export function validateSelection(items, quantities) {
  let total = 0;
  for (const [id, qty] of Object.entries(quantities)) {
    const item = items.get(id);
    if (!item || !item.available || !Number.isInteger(qty) || qty < 1 || qty > 1) return false;
    total += qty * (item.pieces || 1);
  }
  return total >= 1 && total <= 5;
}

export function changeQuantity(items, quantities, id, delta) {
  if (!items.has(id) || ![1, -1].includes(delta)) return quantities;
  const next = { ...quantities };
  const qty = (next[id] || 0) + delta;
  if (qty < 0) return quantities;
  if (qty === 0) delete next[id]; else next[id] = qty;
  if (Object.keys(next).length === 0 || validateSelection(items, next)) return next;
  return quantities;
}

function init(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = 'true';
  const cards = [...root.querySelectorAll('[data-item]')];
  const items = new Map(cards.map(card => [card.dataset.id, {
    id: card.dataset.id, product: card.dataset.product, name: card.dataset.name,
    category: card.dataset.categoryName, sku: card.dataset.sku,
    available: card.dataset.available === 'true', card
  }]));
  const bundle = root.querySelector('[data-bundle]');
  if (bundle) items.set(bundle.dataset.id, {
    id: bundle.dataset.id, product: bundle.dataset.product, name: bundle.dataset.name,
    sku: bundle.dataset.sku, contents: bundle.dataset.contents, available: bundle.dataset.available === 'true', pieces: 5, card: bundle
  });
  const storageKey = 'stonovia-trade-sample-selection-v1';
  let quantities = {};
  let category = 'all';
  const form = root.querySelector('#TradeSamplesForm');
  const submit = root.querySelector('[data-submit]');
  const body = root.querySelector('[data-request-body]');
  const error = root.querySelector('[data-form-error]');
  const success = root.querySelector('[data-success]');
  try {
    if (success) sessionStorage.removeItem(storageKey);
    else {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
      if (saved && typeof saved === 'object' && !Array.isArray(saved) && validateSelection(items, saved)) quantities = saved;
    }
  } catch (_) { /* Selection works even when browser storage is unavailable. */ }
  function updateShippingVisibility() {
    root.dataset.shipping = String(root.querySelector("#TradeSampleRequest").getBoundingClientRect().top < window.innerHeight - 100);
  }
  function render() {
    updateShippingVisibility();
    const total = selectionTotal(items, quantities);
    root.dataset.full = String(total === 5);
    root.querySelector('[data-next]').hidden = total !== 5 || Boolean(success);
    root.querySelector('[data-clear]').hidden = total === 0;
    if (bundle) {
      const selected = Boolean(quantities[bundle.dataset.id]);
      const choose = root.querySelector('[data-choose-set]');
      choose.hidden = false;
      choose.setAttribute('aria-pressed', String(selected));
      choose.setAttribute('aria-label', selected ? 'Deselect this set' : 'Choose this set');
      root.querySelector('[data-bundle-next]').hidden = !selected;
      choose.disabled = !selected && (total === 5 || !items.get(bundle.dataset.id).available);
      choose.textContent = selected ? 'Selected' : total > 0 && total < 5 ? 'Replace with this set' : 'Choose this set';
      root.querySelector('[data-set-note]').textContent = !items.get(bundle.dataset.id).available
        ? 'Currently unavailable. Build your own selection below.'
        : selected ? '1 set · 5 pieces. Click Selected again to deselect.'
        : total === 5 ? 'Clear your selection to choose this set.'
        : total > 0 ? 'This replaces your current individual samples.' : 'Or build your own selection below.';
    }
    root.querySelector('[data-count]').textContent = `${total} of 5 pieces selected`;
    root.querySelector('[data-limit]').textContent = total === 5 ? 'All 5 pieces selected. Remove a piece to choose another.' : `${5 - total} more ${5 - total === 1 ? 'piece' : 'pieces'} available. Choose a different sample for each piece.`;
    for (const item of items.values()) {
      const qty = quantities[item.id] || 0;
      item.card.dataset.selected = String(qty > 0);
      const pick = item.card.querySelector('[data-pick]');
      if (!pick) continue;
      pick.textContent = qty > 0 ? 'SELECTED' : 'Select';
      pick.setAttribute('aria-pressed', String(qty > 0));
      pick.setAttribute('aria-label', `${qty > 0 ? 'Deselect' : 'Select'} ${item.name}`);
      pick.disabled = qty === 0 && (total === 5 || !item.available);

    }
    const list = root.querySelector('[data-selection]');
    list.replaceChildren();
    const lines = [];
    for (const [id, qty] of Object.entries(quantities)) {
      const item = items.get(id);
      const li = document.createElement('li');
      const text = document.createElement('span');
      text.textContent = item.pieces === 5 ? `${item.name} × ${qty} set (5 pieces)` : `${item.name} × ${qty}`;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove all ${item.name}`);
      remove.addEventListener('click', () => { delete quantities[id]; render(); });
      li.append(text, remove);
      list.append(li);
      lines.push(item.pieces === 5 ? `${item.name} × ${qty} set (5 pieces)` : `${item.name} × ${qty}`);
    }
    root.querySelector('[data-selection-empty]').hidden = total > 0;
    const review = root.querySelector('[data-review-list]');
    if (review) review.textContent = total ? `${total} sample ${total === 1 ? 'piece' : 'pieces'} requested\n${lines.join('\n')}` : 'Please select at least one sample above.';
    if (body) body.value = total ? `TRADE SAMPLE REQUEST — staff review required\nTotal pieces: ${total}\n${Object.entries(quantities).map(([id, qty]) => { const item = items.get(id); return `${item.name}${item.contents ? ' (' + item.contents + ')' : ''} | Quantity: ${qty} | Pieces: ${qty * (item.pieces || 1)} | SKU: ${item.sku || 'Not assigned'} | Variant: ${id}`; }).join('\n')}` : '';
    if (submit) submit.disabled = !validateSelection(items, quantities);
    if (error) error.hidden = true;
    try { sessionStorage.setItem(storageKey, JSON.stringify(quantities)); } catch (_) {}
  }
  root.querySelector('[data-clear]').addEventListener('click', () => { quantities = {}; render(); });
  root.querySelector('[data-choose-set]')?.addEventListener('click', () => {
    if (quantities[bundle.dataset.id]) { delete quantities[bundle.dataset.id]; render(); return; }
    const next = { [bundle.dataset.id]: 1 };
    if (validateSelection(items, next)) { quantities = next; render(); }
  });
  for (const card of cards) {
    const pick = card.querySelector('[data-pick]');
    card.addEventListener('click', () => {
      if (!pick || pick.disabled) return;
      quantities = changeQuantity(items, quantities, card.dataset.id, quantities[card.dataset.id] ? -1 : 1);
      render();
    });
  }

  function filter() {
    const query = root.querySelector('[data-search]').value.trim().toLowerCase();
    let visible = 0;
    for (const card of cards) {
      const match = (category === 'all' || card.dataset.category === category) && `${card.dataset.name} ${card.dataset.categoryName}`.toLowerCase().includes(query);
      card.hidden = !match;
      if (match) visible++;
    }
    root.querySelector('[data-empty]').hidden = visible > 0;
  }
  root.querySelector('[data-search]').addEventListener('input', filter);
  for (const link of root.querySelectorAll('a[href^="#"]')) {
    link.addEventListener('click', event => {
      const target = root.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      target.tabIndex = -1;
      target.focus({ preventScroll: true });
      const alignTarget = () => window.scrollTo({
        top: window.scrollY + target.getBoundingClientRect().top - (parseFloat(getComputedStyle(target).scrollMarginTop) || 0),
        behavior: 'instant'
      });
      alignTarget();
      // The theme changes header positioning on scroll; align again after layout settles.
      requestAnimationFrame(() => requestAnimationFrame(alignTarget));
    }, { capture: true });
  }
  for (const button of root.querySelectorAll('[data-filter]')) {
    const count = cards.filter(card => button.dataset.filter === 'all' || card.dataset.category === button.dataset.filter).length;
    button.querySelector('[data-filter-count]').textContent = String(count);
    button.addEventListener('click', () => {
      category = button.dataset.filter;
      root.querySelectorAll('[data-filter]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
      filter();
    });
  }
  form?.addEventListener('submit', event => {
    const country = root.querySelector('#ts-country');
    if (country && !['United States', 'Canada'].includes(country.value)) {
      event.preventDefault();
      if (error) { error.hidden = false; error.textContent = 'Select United States or Canada for sample shipping.'; }
      country.focus();
      return;
    }
    if (!validateSelection(items, quantities)) {
      event.preventDefault();
      if (error) { error.hidden = false; error.textContent = 'Choose up to 5 different samples, or select the 5-piece starter set.'; }
      return;
    }
    render();
  });
  // Keep the next-step bar out of the way while the shipping form is in view.
  let scrollPending = false;
  window.addEventListener('scroll', () => {
    if (scrollPending) return;
    scrollPending = true;
    requestAnimationFrame(() => { updateShippingVisibility(); scrollPending = false; });
  }, { passive: true });
  window.addEventListener('resize', updateShippingVisibility);
  render();
  if (success) { success.focus(); success.scrollIntoView({ block: 'center' }); }
}
if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-trade-samples]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-trade-samples]').forEach(init));
}
